import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Crosshair, RefreshCw, Flame } from 'lucide-react';
import Navigation from '@/components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const probColor = (p) => p >= 20 ? 'text-brand-lime' : p >= 15 ? 'text-brand-cyan' : 'text-white';

const HRZone = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/mlb/hr-zone`);
      setData(res.data);
    } catch (e) {
      console.error('hr zone error', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const goToPlayer = (pid, name) => {
    if (pid) navigate(`/players?pid=${pid}&name=${encodeURIComponent(name || '')}`);
  };

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
                <Crosshair className="h-8 w-8 text-brand-lime" />
                HR Zone
              </h1>
              <p className="text-muted-foreground mt-1">Today's home run projections — games, batters, pitchers & lineups</p>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-[#1e3a5f] text-white hover:bg-[#112240]" disabled={refreshing} data-testid="refresh-hrzone-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-40 skeleton rounded-lg" />)}</div>
          ) : !data || (!data.games.length && !data.batters.length) ? (
            <Card className="bg-[#0f1e36] border-[#1e3a5f]"><CardContent className="p-12 text-center text-muted-foreground">No HR projections available right now.</CardContent></Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="hrzone-summary">
                <Badge variant="outline" className="border-brand-lime text-brand-lime text-sm px-3 py-1">
                  {data.projected_total} projected HRs
                </Badge>
                <Badge variant="outline" className="border-brand-cyan text-brand-cyan text-sm px-3 py-1">
                  {data.games_count} games
                </Badge>
                <span className="text-xs text-muted-foreground">{data.date}{data.last_updated ? ` • Updated ${data.last_updated}` : ''}</span>
              </div>

              <Tabs defaultValue="games">
                <TabsList className="bg-[#0f1e36] border border-[#1e3a5f] p-1 mb-6 flex-wrap h-auto">
                  {['games', 'batters', 'pitchers', 'teams'].map(t => (
                    <TabsTrigger key={t} value={t} className="data-[state=active]:bg-brand-cyan data-[state=active]:text-[#0a1628] uppercase font-bold" data-testid={`hrzone-tab-${t}`}>
                      {t}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {/* GAMES */}
                <TabsContent value="games">
                  <div className="grid md:grid-cols-2 gap-4">
                    {data.games.map((g, i) => (
                      <Card key={i} className="bg-[#0f1e36] border-[#1e3a5f]" data-testid={`hrzone-game-${i}`}>
                        <CardContent className="p-5">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <div className="font-heading text-lg font-bold text-white uppercase">{g.away} @ {g.home}</div>
                              <div className="text-xs text-muted-foreground">{g.park}</div>
                            </div>
                            <div className="text-right">
                              <div className={`font-mono text-2xl font-bold ${g.total >= 2.5 ? 'text-brand-lime' : 'text-brand-cyan'}`}>{g.total}</div>
                              <div className="text-[10px] text-muted-foreground uppercase">Proj HRs</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm mb-3">
                            <span className="text-muted-foreground">{g.away}: <span className="text-white font-mono">{g.away_hrs}</span></span>
                            <span className="text-muted-foreground">{g.home}: <span className="text-white font-mono">{g.home_hrs}</span></span>
                          </div>
                          {g.dist && Object.keys(g.dist).length > 0 && (
                            <div className="grid grid-cols-6 gap-1 mb-3">
                              {Object.entries(g.dist).map(([k, v]) => (
                                <div key={k} className="bg-[#0a1628] rounded p-1.5 text-center">
                                  <div className="text-[9px] text-muted-foreground">{k} HR</div>
                                  <div className="text-xs font-mono text-white">{v}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {g.top_hitters.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-2 border-t border-[#1e3a5f]">
                              {g.top_hitters.map((h, j) => (
                                <button key={j} onClick={() => goToPlayer(h.player_id, h.name)} className="text-xs px-2 py-1 rounded bg-brand-lime/10 text-brand-lime border border-brand-lime/30 hover:bg-brand-lime/20 transition-colors" data-testid={`hrzone-game-${i}-hitter-${j}`}>
                                  <Flame className="h-3 w-3 inline mr-1" />{h.name} {h.prob}%
                                </button>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                {/* BATTERS */}
                <TabsContent value="batters">
                  <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                    <CardContent className="p-4 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-muted-foreground text-[10px] uppercase border-b border-[#1e3a5f]">
                            <th className="text-left py-2 px-1">Batter</th>
                            <th className="text-center py-2 px-1">Team</th>
                            <th className="text-center py-2 px-1" title="Opposing pitcher">Vs Pitcher</th>
                            <th className="text-center py-2 px-1" title="HR probability">HR %</th>
                            <th className="text-center py-2 px-1" title="Fair value odds">Fair</th>
                            <th className="text-center py-2 px-1">FD</th>
                            <th className="text-center py-2 px-1">DK</th>
                            <th className="text-center py-2 px-1">CZ</th>
                            <th className="text-center py-2 px-1">MG</th>
                            <th className="text-center py-2 px-1" title="Season HRs">HR</th>
                            <th className="text-center py-2 px-1" title="Last 20 games HRs">L20</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.batters.map((b, i) => (
                            <tr key={i} className="border-b border-[#1e3a5f]/40 table-row-hover" data-testid={`hrzone-batter-${i}`}>
                              <td className="py-2 px-1">
                                <button onClick={() => goToPlayer(b.player_id, b.name)} className="text-white font-medium hover:text-brand-cyan transition-colors">{b.name}</button>
                                {b.boost && (
                                  <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-orange-500/15 text-orange-400 border border-orange-500/30 px-1.5 py-0.5 rounded-full" data-testid={`hrzone-boost-${i}`}>
                                    🔥 Boost
                                  </span>
                                )}
                              </td>
                              <td className="text-center"><span className="sport-icon sport-mlb text-[9px] px-1.5 py-0.5">{b.team}</span></td>
                              <td className="text-center text-muted-foreground">{b.pitcher}</td>
                              <td className={`text-center font-mono font-bold ${probColor(b.prob)}`}>{b.prob}%</td>
                              <td className="text-center font-mono text-brand-cyan">{b.fair_odds}</td>
                              <td className="text-center font-mono text-white">{b.books?.FD || '—'}</td>
                              <td className="text-center font-mono text-white">{b.books?.DK || '—'}</td>
                              <td className="text-center font-mono text-white">{b.books?.CZ || '—'}</td>
                              <td className="text-center font-mono text-white">{b.books?.MG || '—'}</td>
                              <td className="text-center font-mono text-white">{b.season_hr ?? '—'}</td>
                              <td className="text-center font-mono text-muted-foreground">{b.l20_hr ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* PITCHERS */}
                <TabsContent value="pitchers">
                  <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                    <CardContent className="p-4 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-muted-foreground text-[10px] uppercase border-b border-[#1e3a5f]">
                            <th className="text-left py-2 px-1">Pitcher</th>
                            <th className="text-center py-2 px-1">Team</th>
                            <th className="text-center py-2 px-1">Opp</th>
                            <th className="text-center py-2 px-1" title="Projected HRs allowed">Proj HRs</th>
                            <th className="text-center py-2 px-1" title="Projected innings">Inn</th>
                            <th className="text-center py-2 px-1" title="Season HRs allowed">Season HR</th>
                            <th className="text-center py-2 px-1" title="Last 20 games">L20 HR</th>
                            <th className="text-center py-2 px-1" title="Bad pitches over the heart of the plate">Meatball%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.pitchers.map((p, i) => (
                            <tr key={i} className={`border-b border-[#1e3a5f]/40 table-row-hover ${p.proj_hrs >= 0.9 ? 'bg-brand-lime/5' : ''}`} data-testid={`hrzone-pitcher-${i}`}>
                              <td className="py-2 px-1">
                                <button onClick={() => goToPlayer(p.player_id, p.name)} className="text-white font-medium hover:text-brand-cyan transition-colors">{p.name}</button>
                              </td>
                              <td className="text-center"><span className="sport-icon sport-mlb text-[9px] px-1.5 py-0.5">{p.team}</span></td>
                              <td className="text-center text-muted-foreground">{p.opp}</td>
                              <td className={`text-center font-mono font-bold ${p.proj_hrs >= 0.9 ? 'text-brand-lime' : 'text-white'}`}>{p.proj_hrs ?? '—'}</td>
                              <td className="text-center font-mono text-muted-foreground">{p.innings ?? '—'}</td>
                              <td className="text-center font-mono text-white">{p.season_hr ?? '—'} ({p.season_per_game ?? '—'}/g)</td>
                              <td className="text-center font-mono text-white">{p.l20_hr ?? '—'}</td>
                              <td className="text-center font-mono text-brand-cyan">{p.meatball_pct || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* TEAMS (lineups + bullpens) */}
                <TabsContent value="teams">
                  <div className="grid lg:grid-cols-2 gap-4">
                    <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                      <CardContent className="p-4 overflow-x-auto">
                        <h3 className="font-heading text-lg font-bold text-white uppercase mb-3">Lineup Projections</h3>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-muted-foreground text-[10px] uppercase border-b border-[#1e3a5f]">
                              <th className="text-left py-2">Team</th>
                              <th className="text-center py-2">Vs</th>
                              <th className="text-center py-2">Proj HRs</th>
                              <th className="text-center py-2">0</th>
                              <th className="text-center py-2">1</th>
                              <th className="text-center py-2">2</th>
                              <th className="text-center py-2">3+</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.lineups.map((l, i) => (
                              <tr key={i} className="border-b border-[#1e3a5f]/40" data-testid={`hrzone-lineup-${i}`}>
                                <td className="py-2 text-white font-medium">{l.nickname} <span className="text-muted-foreground text-xs">({l.team})</span></td>
                                <td className="text-center text-muted-foreground">{l.pitcher}</td>
                                <td className={`text-center font-mono font-bold ${l.proj >= 1.4 ? 'text-brand-lime' : 'text-white'}`}>{l.proj ?? '—'}</td>
                                <td className="text-center font-mono text-muted-foreground">{l.dist?.['0']}</td>
                                <td className="text-center font-mono text-muted-foreground">{l.dist?.['1']}</td>
                                <td className="text-center font-mono text-muted-foreground">{l.dist?.['2']}</td>
                                <td className="text-center font-mono text-muted-foreground">{l.dist?.['3+']}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                      <CardContent className="p-4 overflow-x-auto">
                        <h3 className="font-heading text-lg font-bold text-white uppercase mb-3">Bullpen HRs Allowed</h3>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-muted-foreground text-[10px] uppercase border-b border-[#1e3a5f]">
                              <th className="text-left py-2">Bullpen</th>
                              <th className="text-center py-2">Opp</th>
                              <th className="text-center py-2">Season HR (/g)</th>
                              <th className="text-center py-2">L20 HR (/g)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.bullpens.map((b, i) => (
                              <tr key={i} className="border-b border-[#1e3a5f]/40" data-testid={`hrzone-bullpen-${i}`}>
                                <td className="py-2 text-white font-medium">{b.nickname} <span className="text-muted-foreground text-xs">({b.team})</span></td>
                                <td className="text-center text-muted-foreground">{b.opp}</td>
                                <td className="text-center font-mono text-white">{b.season_hr ?? '—'} ({b.season_per_game ?? '—'})</td>
                                <td className="text-center font-mono text-white">{b.l20_hr ?? '—'} ({b.l20_per_game ?? '—'})</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default HRZone;
